import { motion } from "framer-motion";
import {
  Crosshair,
  Shirt,
  Layers,
  Presentation,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/constants/testIds";
import { useT } from "@/lib/i18n";

const ICONS = [Crosshair, Shirt, Layers, Presentation];

export const Services = () => {
  const { t } = useT();
  const s = t.services;
  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-white/10"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
        <div className="md:col-span-4">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#ff3b00] mb-4">
            {s.eyebrow}
          </div>
          <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
            {s.titleA}<br />
            <span className="text-white/40">{s.titleB}</span>
          </h2>
        </div>
        <div className="md:col-span-5 md:col-start-8">
          <p className="font-mono text-sm md:text-base text-white/60 leading-relaxed">
            {s.lede}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
        {s.items.map((item, i) => {
          const Icon = ICONS[i];
          const num = String(i + 1).padStart(2, "0");
          return (
            <motion.div
              key={i}
              data-testid={SERVICES.card(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
              className="group relative p-8 lg:p-10 border-r border-b border-white/10 bg-[#0a0a0a] hover:bg-[#141414] transition-colors duration-300 min-h-[340px] flex flex-col"
            >
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono text-xs tracking-[0.3em] text-white/40 group-hover:text-[#ff3b00] transition-colors">
                  [ {num} ]
                </span>
                <Icon className="w-6 h-6 text-white/60 group-hover:text-[#ff3b00] transition-colors" strokeWidth={1.5} />
              </div>

              <h3 className="font-display uppercase text-2xl md:text-3xl leading-[0.95] tracking-tight mb-4 text-white">
                {item.title}
              </h3>

              <p className="font-mono text-sm leading-relaxed text-white/60 flex-1">
                {item.body}
              </p>

              <div className="mt-8 pt-4 border-t border-white/10 flex flex-wrap gap-1.5">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="font-mono text-[10px] tracking-[0.2em] text-white/50 px-2 py-1 border border-white/15 group-hover:border-[#ff3b00]/60 group-hover:text-white transition-colors"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <ArrowUpRight
                className="absolute right-6 bottom-6 w-4 h-4 text-white/0 group-hover:text-[#ff3b00] transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                strokeWidth={2}
              />
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};
